import { PageDetailLoading } from '@affine/component/page-detail-skeleton';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import { type I18nInstance, useI18n } from '@affine/i18n';
import { AttachmentIcon, EmailIcon } from '@blocksuite/icons/rc';
import {
  type ClipboardEvent as ReactClipboardEvent,
  type DragEvent as ReactDragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type MutableRefObject,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { mailComposeTextToHtml } from './mail-compose-html';
import { writeMailAttachmentClipboardPayload } from './mail-doc-import';
import * as styles from './mail-fullscreen-view.css';
import {
  type MailRemoteImage,
  type SanitizedMailHtml,
  sanitizeMailHtml,
} from './mail-html-sanitizer';
import {
  getMailRemoteImageCacheKey,
  readBlobAsDataUrl,
  readCachedMailRemoteImageDataUrl,
  writeCachedMailRemoteImage,
} from './mail-remote-image-cache';
import {
  formatMailBytes,
  formatMailDate,
  formatMailSenderDisplayName,
  type MailAttachmentInfo,
  type MailMessageBodyInfo,
  stripHtmlForMailPreview,
} from './types';

const MAIL_I18N_PREFIX = 'com.streamlain.mail';
const MAIL_BODY_MIN_LOADING_MS = 120;

export type MailPreviewMessage = {
  id: string;
  subject?: string | null;
  from?: string | null;
  fromName?: string | null;
  fromAddress?: string | null;
  toAddresses?: readonly string[] | null;
  receivedAt?: string | null;
  sentAt?: string | null;
  createdAt?: string | null;
  snippet?: string | null;
  sizeBytes?: string | number | null;
};

export function getMailRemoteImageProxyUrl({
  workspaceId,
  messageId,
  image,
}: {
  workspaceId: string;
  messageId: string;
  image: MailRemoteImage;
}): string {
  return `/api/workspace/${encodeURIComponent(
    workspaceId
  )}/mail/messages/${encodeURIComponent(
    messageId
  )}/remote-images/${encodeURIComponent(image.id)}`;
}

function joinMailAddresses(addresses: readonly string[] | null | undefined) {
  return (addresses ?? []).map(address => address.trim()).filter(Boolean);
}

function getRecipientSummary(
  addresses: readonly string[] | null | undefined,
  i18n: I18nInstance
): string {
  const recipients = joinMailAddresses(addresses);
  if (recipients.length === 0) return mailText(i18n, 'message.noRecipients');
  if (recipients.length <= 2) return recipients.join(', ');
  return `${recipients.slice(0, 2).join(', ')} +${recipients.length - 2}`;
}

function getRecipientTitle(addresses: readonly string[] | null | undefined) {
  return joinMailAddresses(addresses).join(', ');
}

function getSenderTitle(message: MailPreviewMessage, fallbackName: string) {
  const address = message.fromAddress?.trim() || message.from?.trim();
  const name = message.fromName?.trim() || fallbackName;
  if (!address) return name;
  if (!name || name === address) return address;
  return `${name} <${address}>`;
}

export async function fetchMailRemoteImageDataUrl({
  workspaceId,
  messageId,
  image,
}: {
  workspaceId: string;
  messageId: string;
  image: MailRemoteImage;
}): Promise<string> {
  const cacheKey = getMailRemoteImageCacheKey({
    workspaceId,
    messageId,
    imageId: image.id,
  });
  const cached = await readCachedMailRemoteImageDataUrl(cacheKey);
  if (cached) return cached;

  const response = await workspaceApiFetch(
    getMailRemoteImageProxyUrl({ workspaceId, messageId, image }),
    {
      credentials: 'include',
      referrerPolicy: 'no-referrer',
    },
    {
      workspaceId,
      category: 'workspace-api',
      mode: 'background',
      priority: 'visible',
    }
  );
  if (!response.ok) {
    throw new Error(`Mail remote image proxy failed: ${response.status}`);
  }
  const blob = await response.blob();
  await writeCachedMailRemoteImage(cacheKey, blob);
  return readBlobAsDataUrl(blob);
}

export function MailMessagePreview({
  actions,
  body,
  canLoadRemoteImages = false,
  canTrustRemoteImagesSender = false,
  className,
  error,
  fitHtmlToWidth = false,
  htmlFrameRef,
  isLoading,
  loadRemoteImages = false,
  message,
  messageActions,
  onDownloadAttachment,
  onContentScroll,
  onLoadRemoteImages,
  onTrustRemoteImagesSender,
  onSanitizedHtmlChange,
  workspaceId,
}: {
  message: MailPreviewMessage | null;
  body?: MailMessageBodyInfo;
  actions?: ReactNode;
  messageActions?: ReactNode;
  className?: string;
  isLoading?: boolean;
  error?: string;
  fitHtmlToWidth?: boolean;
  htmlFrameRef?: RefObject<HTMLIFrameElement | null>;
  workspaceId?: string;
  loadRemoteImages?: boolean;
  canLoadRemoteImages?: boolean;
  onLoadRemoteImages?: () => void;
  canTrustRemoteImagesSender?: boolean;
  onTrustRemoteImagesSender?: () => void;
  onDownloadAttachment?: (attachment: MailAttachmentInfo) => void;
  onContentScroll?: (scrollTop: number) => void;
  onSanitizedHtmlChange?: (sanitizedHtml: SanitizedMailHtml | null) => void;
}) {
  const i18n = useI18n();
  const [remoteImageDataUrls, setRemoteImageDataUrls] = useState<
    Record<string, string>
  >({});
  const [readyHtmlFrameContent, setReadyHtmlFrameContent] = useState<
    string | null
  >(null);
  const [visibleHtmlFrameContent, setVisibleHtmlFrameContent] = useState<
    string | null
  >(null);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<string[]>(
    []
  );
  const attachmentSelectionAnchorRef = useRef<string | null>(null);
  const internalHtmlFrameRef = useRef<HTMLIFrameElement | null>(null);
  const setHtmlFrameRef = useCallback(
    (node: HTMLIFrameElement | null) => {
      internalHtmlFrameRef.current = node;
      if (htmlFrameRef) {
        (htmlFrameRef as MutableRefObject<HTMLIFrameElement | null>).current =
          node;
      }
    },
    [htmlFrameRef]
  );
  const activeMessageId = message?.id ?? null;
  useEffect(() => {
    setRemoteImageDataUrls({});
    setSelectedAttachmentIds([]);
    attachmentSelectionAnchorRef.current = null;
  }, [activeMessageId, body?.bodyHtml]);
  const attachments = useMemo(
    () => body?.attachments ?? [],
    [body?.attachments]
  );
  const selectedAttachmentIdSet = useMemo(
    () => new Set(selectedAttachmentIds),
    [selectedAttachmentIds]
  );
  const selectedAttachments = useMemo(
    () =>
      attachments.filter(attachment =>
        selectedAttachmentIdSet.has(attachment.id)
      ),
    [attachments, selectedAttachmentIdSet]
  );
  const selectAttachment = useCallback(
    (attachment: MailAttachmentInfo, event: ReactMouseEvent<HTMLElement>) => {
      const ids = attachments.map(item => item.id);
      if (event.shiftKey && attachmentSelectionAnchorRef.current) {
        const anchorIndex = ids.indexOf(attachmentSelectionAnchorRef.current);
        const targetIndex = ids.indexOf(attachment.id);
        if (anchorIndex !== -1 && targetIndex !== -1) {
          const start = Math.min(anchorIndex, targetIndex);
          const end = Math.max(anchorIndex, targetIndex);
          setSelectedAttachmentIds(ids.slice(start, end + 1));
          return;
        }
      }
      attachmentSelectionAnchorRef.current = attachment.id;
      if (event.metaKey || event.ctrlKey) {
        setSelectedAttachmentIds(previous =>
          previous.includes(attachment.id)
            ? previous.filter(id => id !== attachment.id)
            : [...previous, attachment.id]
        );
        return;
      }
      setSelectedAttachmentIds([attachment.id]);
    },
    [attachments]
  );
  const getAttachmentDragSelection = useCallback(
    (attachment: MailAttachmentInfo) => {
      return selectedAttachmentIdSet.has(attachment.id) &&
        selectedAttachments.length > 0
        ? selectedAttachments
        : [attachment];
    },
    [selectedAttachmentIdSet, selectedAttachments]
  );
  const copySelectedAttachments = useCallback(
    (event: ReactClipboardEvent<HTMLElement>) => {
      if (
        !event.clipboardData ||
        !workspaceId ||
        selectedAttachments.length === 0
      ) {
        return;
      }
      writeMailAttachmentClipboardPayload(event.clipboardData, {
        kind: 'streamlain-mail-attachments',
        workspaceId,
        attachments: selectedAttachments,
      });
      event.preventDefault();
      event.stopPropagation();
    },
    [selectedAttachments, workspaceId]
  );
  const startAttachmentDrag = useCallback(
    (attachment: MailAttachmentInfo, event: ReactDragEvent<HTMLElement>) => {
      if (!workspaceId) {
        event.preventDefault();
        return;
      }
      const attachments = getAttachmentDragSelection(attachment);
      writeMailAttachmentClipboardPayload(event.dataTransfer, {
        kind: 'streamlain-mail-attachments',
        workspaceId,
        attachments,
      });
      event.dataTransfer.effectAllowed = 'copy';
    },
    [getAttachmentDragSelection, workspaceId]
  );
  const displayBodyHtml = body?.bodyHtml?.trim()
    ? body.bodyHtml
    : mailComposeTextToHtml(body?.bodyText ?? '');
  const sanitizedHtml = useMemo(
    () =>
      sanitizeMailHtml(displayBodyHtml, body?.attachments ?? [], {
        remoteImagePlaceholderLabel: mailText(
          i18n,
          'detail.remoteImagePlaceholder'
        ),
        resolveRemoteImageUrl:
          loadRemoteImages && activeMessageId && workspaceId
            ? image => remoteImageDataUrls[image.id] ?? null
            : undefined,
      }),
    [
      activeMessageId,
      body?.attachments,
      displayBodyHtml,
      i18n,
      loadRemoteImages,
      remoteImageDataUrls,
      workspaceId,
    ]
  );
  useEffect(() => {
    onSanitizedHtmlChange?.(sanitizedHtml);
  }, [onSanitizedHtmlChange, sanitizedHtml]);
  useEffect(() => {
    if (
      !loadRemoteImages ||
      !activeMessageId ||
      !workspaceId ||
      !sanitizedHtml?.remoteImages.length
    ) {
      return;
    }
    const missingImages = Array.from(
      new Map(
        sanitizedHtml.remoteImages
          .filter(image => !remoteImageDataUrls[image.id])
          .map(image => [image.id, image])
      ).values()
    );
    if (missingImages.length === 0) {
      return;
    }

    let cancelled = false;
    void (async () => {
      const loaded = await Promise.allSettled(
        missingImages.map(async image => {
          const url = await fetchMailRemoteImageDataUrl({
            workspaceId,
            messageId: activeMessageId,
            image,
          });
          return [image.id, url] as const;
        })
      );
      const nextUrls = Object.fromEntries(
        loaded
          .filter(
            (
              result
            ): result is PromiseFulfilledResult<readonly [string, string]> =>
              result.status === 'fulfilled'
          )
          .map(result => result.value)
      );
      if (cancelled) {
        return;
      }
      if (Object.keys(nextUrls).length > 0) {
        setRemoteImageDataUrls(current => ({ ...current, ...nextUrls }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeMessageId,
    loadRemoteImages,
    remoteImageDataUrls,
    sanitizedHtml?.remoteImages,
    workspaceId,
  ]);
  const bodyText = useMemo(() => {
    if (body?.bodyText) return body.bodyText;
    return stripHtmlForMailPreview(body?.bodyHtml);
  }, [body]);
  const htmlFrameContent = sanitizedHtml?.srcDoc ?? null;
  const htmlFrameReady =
    htmlFrameContent !== null && readyHtmlFrameContent === htmlFrameContent;
  useEffect(() => {
    if (!htmlFrameReady || htmlFrameContent === null) {
      return;
    }

    let firstPaint = 0;
    let secondPaint = 0;
    let revealTimeout = 0;
    firstPaint = requestAnimationFrame(() => {
      secondPaint = requestAnimationFrame(() => {
        revealTimeout = window.setTimeout(() => {
          setVisibleHtmlFrameContent(htmlFrameContent);
        }, MAIL_BODY_MIN_LOADING_MS);
      });
    });

    return () => {
      cancelAnimationFrame(firstPaint);
      cancelAnimationFrame(secondPaint);
      window.clearTimeout(revealTimeout);
    };
  }, [htmlFrameContent, htmlFrameReady]);
  const htmlFrameVisible =
    htmlFrameReady && visibleHtmlFrameContent === htmlFrameContent;
  useEffect(() => {
    if (!htmlFrameVisible || !onContentScroll) return;
    const frameWindow = internalHtmlFrameRef.current?.contentWindow;
    if (!frameWindow) return;

    const handleScroll = () =>
      onContentScroll(Math.max(0, frameWindow.scrollY));
    frameWindow.addEventListener('scroll', handleScroll, { passive: true });
    return () => frameWindow.removeEventListener('scroll', handleScroll);
  }, [htmlFrameVisible, onContentScroll]);
  useEffect(() => {
    if (!fitHtmlToWidth || !sanitizedHtml) return;
    const frame = internalHtmlFrameRef.current;
    if (!frame) return;

    let frameResizeObserver: ResizeObserver | null = null;
    let animationFrame = 0;

    const applyFit = () => {
      const doc = frame.contentDocument;
      const root = doc?.getElementById(
        'sl-mail-fit-root'
      ) as HTMLElement | null;
      if (!doc || !root) return;

      root.style.setProperty('zoom', '1');
      const availableWidth =
        doc.documentElement.clientWidth || frame.clientWidth;
      if (availableWidth <= 0) return;

      const rootRect = root.getBoundingClientRect();
      const contentWidth = Array.from(
        root.querySelectorAll<HTMLElement>('*')
      ).reduce(
        (maxWidth, element) =>
          Math.max(
            maxWidth,
            element.scrollWidth,
            element.getBoundingClientRect().right - rootRect.left
          ),
        Math.max(root.scrollWidth, rootRect.width)
      );
      const scale =
        contentWidth > availableWidth ? availableWidth / contentWidth : 1;
      root.style.setProperty('zoom', scale < 1 ? String(scale) : '1');
      doc.documentElement.style.overflowX = 'hidden';
      doc.body.style.overflowX = 'hidden';
    };

    const scheduleFit = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(applyFit);
    };

    const observeFrameContent = () => {
      scheduleFit();
    };

    frame.addEventListener('load', observeFrameContent);
    observeFrameContent();
    if (typeof ResizeObserver !== 'undefined') {
      frameResizeObserver = new ResizeObserver(scheduleFit);
      frameResizeObserver.observe(frame);
    }

    return () => {
      cancelAnimationFrame(animationFrame);
      frame.removeEventListener('load', observeFrameContent);
      frameResizeObserver?.disconnect();
    };
  }, [fitHtmlToWidth, sanitizedHtml]);
  const paneClassName = className
    ? `${styles.detailPane} ${className}`
    : styles.detailPane;
  const bodyPending = Boolean(
    message && (body === undefined || body.id !== message.id) && !error
  );
  const messagePending = Boolean(
    !error && (bodyPending || (sanitizedHtml && !htmlFrameVisible))
  );

  if (isLoading && !message) {
    return (
      <section className={paneClassName}>
        <div className={styles.detailLoading}>
          <PageDetailLoading />
        </div>
      </section>
    );
  }

  if (!message) {
    return (
      <section className={paneClassName}>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon}>
            <EmailIcon />
          </span>
          <div className={styles.emptyTitle}>
            {mailText(i18n, 'detail.emptyTitle')}
          </div>
          <div className={styles.emptyHint}>
            {mailText(i18n, 'detail.emptyHint')}
          </div>
        </div>
      </section>
    );
  }

  const subject =
    body?.subject || message.subject || mailText(i18n, 'message.noSubject');
  const senderDisplayName = getPreviewSenderName(message, i18n);
  const recipientSummary = getRecipientSummary(message.toAddresses, i18n);
  const recipientTitle = getRecipientTitle(message.toAddresses);

  return (
    <section className={paneClassName}>
      {messagePending ? (
        <div
          aria-busy="true"
          className={styles.detailPaneLoading}
          data-mail-body-loading="true"
        >
          <PageDetailLoading />
        </div>
      ) : null}

      <div className={styles.detailHeaderRegion}>
        <header className={styles.detailHeader}>
          <div className={styles.detailHeaderMain}>
            <h2 className={styles.detailSubject} title={subject}>
              {subject}
            </h2>
            <div className={styles.detailFromRow}>
              <span
                className={styles.detailFromName}
                title={getSenderTitle(message, senderDisplayName)}
              >
                {senderDisplayName}
              </span>
            </div>
            <div className={styles.detailMeta} title={recipientTitle}>
              {mailText(i18n, 'message.toLine', {
                recipients: recipientSummary,
                size: formatMailBytes(body?.sizeBytes ?? message.sizeBytes),
              })}
            </div>
          </div>
          <div className={styles.detailHeaderAside}>
            {actions}
            <span className={styles.detailDate}>
              {formatMailDate(
                message.receivedAt || message.sentAt || message.createdAt
              )}
            </span>
          </div>
        </header>
        {messageActions}
      </div>

      {error ? <div className={styles.errorBanner}>{error}</div> : null}

      {bodyPending ? (
        <div
          className={styles.detailHtmlBody}
          data-mail-body-renderer="pending"
        />
      ) : sanitizedHtml ? (
        <div
          className={styles.detailHtmlBody}
          data-mail-body-state={htmlFrameVisible ? 'ready' : 'loading'}
        >
          {htmlFrameVisible &&
          sanitizedHtml.remoteImages.length > 0 &&
          !loadRemoteImages ? (
            <div className={styles.remoteImageNotice}>
              <span className={styles.remoteImageNoticeText}>
                {mailText(i18n, 'detail.remoteImagesBlocked', {
                  count: sanitizedHtml.remoteImages.length,
                })}
              </span>
              <span className={styles.remoteImageActions}>
                {onLoadRemoteImages ? (
                  <button
                    className={styles.remoteImageButton}
                    onClick={onLoadRemoteImages}
                    disabled={!canLoadRemoteImages}
                    title={
                      canLoadRemoteImages
                        ? mailText(i18n, 'detail.loadRemoteImages')
                        : mailText(i18n, 'offline.toolbarIndicator')
                    }
                  >
                    {mailText(i18n, 'detail.loadRemoteImages')}
                  </button>
                ) : null}
                {onTrustRemoteImagesSender ? (
                  <button
                    className={styles.remoteImageButton}
                    onClick={onTrustRemoteImagesSender}
                    disabled={!canTrustRemoteImagesSender}
                    title={
                      canTrustRemoteImagesSender
                        ? mailText(i18n, 'detail.trustRemoteImagesSender')
                        : mailText(i18n, 'offline.toolbarIndicator')
                    }
                  >
                    {mailText(i18n, 'detail.trustRemoteImagesSender')}
                  </button>
                ) : null}
              </span>
            </div>
          ) : null}
          <iframe
            className={styles.mailHtmlFrame}
            data-mail-body-renderer="html"
            data-ready={htmlFrameVisible ? 'true' : 'false'}
            key={htmlFrameContent}
            ref={setHtmlFrameRef}
            onLoad={() => setReadyHtmlFrameContent(htmlFrameContent)}
            sandbox="allow-popups allow-modals allow-same-origin"
            referrerPolicy="no-referrer"
            srcDoc={sanitizedHtml.srcDoc}
            title={mailText(i18n, 'detail.htmlFrameTitle')}
          />
        </div>
      ) : (
        <div
          className={styles.detailBody}
          data-mail-body-renderer="plaintext"
          onScroll={event => onContentScroll?.(event.currentTarget.scrollTop)}
        >
          {bodyText || message.snippet || ''}
        </div>
      )}

      {attachments.length && (!sanitizedHtml || htmlFrameVisible) ? (
        <div
          className={styles.attachmentList}
          data-mail-attachment-list="true"
          onCopy={copySelectedAttachments}
        >
          {attachments.map(attachment => (
            <MailAttachmentChip
              key={attachment.id}
              attachment={attachment}
              selected={selectedAttachmentIdSet.has(attachment.id)}
              draggable={Boolean(workspaceId)}
              onDownload={onDownloadAttachment}
              onDragStart={event => startAttachmentDrag(attachment, event)}
              onSelect={event => selectAttachment(attachment, event)}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function mailText(
  i18n: I18nInstance,
  key: string,
  options?: Record<string, number | string>
) {
  return i18n.t(`${MAIL_I18N_PREFIX}.${key}`, options);
}

function MailAttachmentChip({
  attachment,
  draggable,
  onDownload,
  onDragStart,
  onSelect,
  selected,
}: {
  attachment: MailAttachmentInfo;
  draggable?: boolean;
  onDownload?: (attachment: MailAttachmentInfo) => void;
  onDragStart?: (event: ReactDragEvent<HTMLButtonElement>) => void;
  onSelect?: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  selected?: boolean;
}) {
  const i18n = useI18n();

  if (!onDownload) {
    return (
      <span className={styles.attachmentChip} title={attachment.filename}>
        <AttachmentIcon />
        {attachment.filename}
      </span>
    );
  }

  return (
    <button
      className={styles.attachmentChip}
      data-selected={selected ? 'true' : undefined}
      draggable={draggable}
      onClick={event => onSelect?.(event)}
      onDoubleClick={() => onDownload(attachment)}
      onDragStart={onDragStart}
      onKeyDown={(event: ReactKeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter') {
          onDownload(attachment);
        }
      }}
      title={mailText(i18n, 'message.downloadAttachment')}
      type="button"
      aria-pressed={selected}
    >
      <AttachmentIcon />
      {attachment.filename}
    </button>
  );
}

function getPreviewSenderName(message: MailPreviewMessage, i18n: I18nInstance) {
  return (
    formatMailSenderDisplayName({
      fromName: message.fromName,
      fromAddress: message.fromAddress ?? message.from,
    }) ||
    message.toAddresses?.join(', ') ||
    mailText(i18n, 'message.unknownSender')
  );
}
